/* ═══════════════════════════════════════════════════════════════════════════
   Zaglavlje — ko je prijavljen, u kojoj firmi radi, i prelazak u drugu firmu.

   ── ZAŠTO JE PREPRAVLJENO ─────────────────────────────────────────────────
   Ranije je zatvaranje spiska radilo ovako:

       setTimeout(() => { if (toggleDrop != 1) setToggleDrop(1) }, 15000)

   i to je stajalo u TELU komponente, ne u efektu. Posledica: svaki render je
   zakazivao NOV tajmer, nijedan se nije poništavao, i oni su se gomilali.
   Tajmer zakazan davno (pri nekom ranijem renderu) umeo je da istekne odmah
   pošto se spisak otvori i zatvori ga u pola izbora — a `setToggleDrop` iz
   tajmera je i sam pravio nov render, dakle i nov tajmer. Otuda utisak da
   „ne prati timeout": pratio je, samo ih je bilo desetine.

   Sada postoji TAČNO JEDAN tajmer, u efektu, sa čišćenjem (`clearTimeout`), i:
     - staje dok je miš nad spiskom (držanje miša = nameram da biram),
     - staje dok je otvorena potvrda (dijalog ne sme da nestane pod rukom),
     - poništava se na zatvaranje i na odjavu komponente.

   ── ŠTA JE JOŠ PROMENJENO ─────────────────────────────────────────────────
   Umesto padajućeg spiska u zaglavlju (koji se bira pa potvrđuje dugmetom),
   otvara se panel sa spiskom firmi: trenutna je označena, klik na drugu vodi
   pravo na potvrdu. Jedan korak manje, a vidi se gde si i gde možeš.

   Zatvara se i na Escape i na klik van panela — nijedno ranije nije radilo.
   ═══════════════════════════════════════════════════════════════════════════ */

import React, { useState, useRef, useEffect, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { AuthContext } from '../Header/auth - context.js';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
/* Isti ekran koji je ranije bio tab „Korisnik" u Administraciji — sada se
   otvara odavde, iz panela, jer su to lična podešavanja a ne šifarnik firme. */
import KorisnikHome from '../components/Administracija/componentsTabs/KorisnikHome';

import './Header.css';

/* Koliko panel stoji otvoren bez dodira. Dovoljno da se pročita spisak, a opet
   se ne ostavlja otvoren nad tuđim ekranom. */
const ZATVARANJE_MS = 15000;

/* Inicijali umesto generičke ikonice čoveka: TMS nema slike korisnika, a dva
   operatera za istim računarom se po ikonici ne razlikuju. */
const inicijali = (ime, prezime) => {
    const a = String(ime || '').trim().charAt(0);
    const b = String(prezime || '').trim().charAt(0);
    return ((a + b) || '?').toUpperCase();
};

/* Boja se IZVODI iz imena, ne bira nasumično: isti čovek ima istu boju na
   svakom računaru i posle svakog osvežavanja, pa avatar postaje prepoznatljiv.
   Zasićenost i svetlina su fiksne — menja se samo nijansa, da inicijali ostanu
   čitljivi na tamnom zaglavlju u obe teme. */
/* Znak firme: prva slova DVE prepoznatljive reči iz naziva, bez pravne forme.
   Bez toga "DOO ŠARČEVIĆ CO VRBAS" daje "DŠ" — slovo D nosi pravna forma, koju
   ima i svaka druga firma u spisku, pa se znaci ne razlikuju. */
const PRAVNE_FORME = new Set(['DOO', 'DOOEL', 'AD', 'OD', 'PR', 'SZR', 'SUR', 'STR', 'CO', 'LTD', 'LLC', 'GMBH', 'SRL', 'SPA']);

const znakFirme = (naziv) => {
    const reci = String(naziv || '')
        .split(/s+/)
        .filter(r => r && !PRAVNE_FORME.has(r.toUpperCase()));
    const izvor = reci.length ? reci : String(naziv || '').split(/s+/);
    return inicijali(izvor[0], izvor[1]);
};

const bojaImena = (tekst) => {
    const s = String(tekst || '');
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
    return `hsl(${h}, 42%, 42%)`;
};

const Auth = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const toast = useRef(null);
    const auth = useContext(AuthContext);
    const history = useHistory();

    const company = useSelector(state => state.company);
    const company_sub_u_sub = useSelector(state => state.company_sub_u_sub);
    const user = useSelector(state => state.user);
    /* Avatar iz ćaskanja; kad ga nema, ostaju inicijali. */
    const avatar = useSelector(state => state.avatar);

    const [otvoren, setOtvoren] = useState(false);
    const [profilOtvoren, setProfilOtvoren] = useState(false);
    const [menja, setMenja] = useState(false);
    /* Menjanje ključa restartuje CSS animaciju trake — bez toga bi traka posle
       pauze nastavila odakle je stala, a tajmer kreće iznova. */
    const [kljucTrake, setKljucTrake] = useState(0);

    const panelRef = useRef(null);
    const dugmeRef = useRef(null);
    const tajmer = useRef(null);
    /* Dok je potvrda otvorena, odbrojavanje stoji: dijalog traži odgovor, a
       panel ispod njega ne sme da nestane. */
    const pauzirano = useRef(false);

    /* Spisak dolazi kao objekat sa prijave ({0: {value,label}, ...}). */
    const firme = useMemo(() => {
        const sve = Object.values(company_sub_u_sub || {});
        return sve.filter(x => x && x.value != null);
    }, [company_sub_u_sub]);

    const imaIzbora = firme.length > 1;

    const punoIme = `${user?.userName || ''} ${user?.userSurname || ''}`.trim();
    const znak = inicijali(user?.userName, user?.userSurname);
    const bojaZnaka = bojaImena(punoIme || znak);

    /* Slika se servira statički sa `/API/files`, pa joj treba adresa servera
       bez `/API` na kraju — isti račun kao u ćaskanju. */
    const avatarSrc = avatar
        ? (process.env.REACT_APP_BACKEND_URL || '').replace(/\/API\/?$/, '') + avatar
        : '';

    /* Jedan znak za oba mesta (traka i panel), da se ne piše dvaput.
       Slika se ne učitava uvek — pokvarena putanja pada na inicijale. */
    const [slikaPukla, setSlikaPukla] = useState(false);
    useEffect(() => { setSlikaPukla(false); }, [avatarSrc]);
    const imaSliku = !!avatarSrc && !slikaPukla;

    const znakAvatara = (klasa) => (
        <span className={klasa + (imaSliku ? ' sa-slikom' : '')}
              style={imaSliku ? undefined : { background: bojaZnaka }} aria-hidden="true">
            {imaSliku
                ? <img src={avatarSrc} alt="" onError={() => setSlikaPukla(true)} />
                : znak}
        </span>
    );

    const zatvori = useCallback(() => setOtvoren(false), []);

    /* Jedan tajmer, u efektu, sa čišćenjem — vidi objašnjenje na vrhu fajla. */
    useEffect(() => {
        if (!otvoren) return undefined;
        if (pauzirano.current) return undefined;

        tajmer.current = setTimeout(() => setOtvoren(false), ZATVARANJE_MS);
        return () => clearTimeout(tajmer.current);
    }, [otvoren, kljucTrake]);

    /* Escape i klik van panela. Oba se vezuju SAMO dok je panel otvoren — stalni
       slušalac na dokumentu bi radio i kad nema šta da zatvori. */
    useEffect(() => {
        if (!otvoren) return undefined;

        const naTaster = (e) => { if (e.key === 'Escape') zatvori(); };
        const naKlik = (e) => {
            if (panelRef.current?.contains(e.target)) return;
            if (dugmeRef.current?.contains(e.target)) return;
            /* Potvrda je u svom sloju van panela — klik po njoj ne sme da zatvori
               ono što joj je povod. */
            if (e.target.closest?.('.p-confirm-dialog, .p-dialog-mask')) return;
            zatvori();
        };

        document.addEventListener('keydown', naTaster);
        document.addEventListener('mousedown', naKlik);
        return () => {
            document.removeEventListener('keydown', naTaster);
            document.removeEventListener('mousedown', naKlik);
        };
    }, [otvoren, zatvori]);

    const zaustaviOdbrojavanje = () => {
        pauzirano.current = true;
        clearTimeout(tajmer.current);
    };

    const nastaviOdbrojavanje = () => {
        pauzirano.current = false;
        /* Nov ključ = nov efekat = nov tajmer i restart trake. */
        setKljucTrake(k => k + 1);
    };

    const planNoviCheck = (token) => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/plan/novi_count', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        })
            .then(res => (res.ok ? res.json() : Promise.reject(res)))
            .then(result => {
                dispatch({ type: 'PLAN_NOVI_SET', value: result.count || 0 });
                dispatch({ type: 'PLAN_NEPROCITANI_SET', value: result.neprocitani || 0 });
            })
            .catch(() => {});
    };

    const infoAlarmCheck = (token) => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/alarms/info', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        })
            .then(res => (res.ok ? res.json() : Promise.reject(res)))
            .then((result) => {
                if (!Array.isArray(result)) return;
                const faktureProvera = result.filter(x => x.TIP === 'FaktureProvera');
                /* Zbir svih grupa stiže gotov iz procedure — vidi App.js. */
                const ukupno = result.filter(x => x.TIP === 'Ukupno');
                dispatch({ type: 'COUNTER', value: ukupno[0]?.ALARMI || 0 });
                dispatch({ type: 'COUNTER_INVOICE_CHECK', value: faktureProvera[0]?.ALARMI || 0 });
            })
            .catch(() => {});
    };

    const promeni = (firma) => {
        if (menja) return;
        setMenja(true);

        fetch(process.env.REACT_APP_BACKEND_URL + '/auth', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + auth.token,
            },
            body: JSON.stringify({ firma_id_new: firma.value }),
        })
            .then(r => (r.ok ? r.json() : Promise.reject(r)))
            .then((data) => {
                const novaFirma = {
                    companyId: data.company.id,
                    companyName: data.company.Naziv,
                    companyState: data.company.Drzava,
                    companyCountryID: data.company.id_drzava,
                };
                /* Firma ide uz token u ostale otvorene tabove (OP-124) — inače bi oni
                   preuzeli token nove firme, a u zaglavlju ostali na staroj. */
                auth.login(user.userId, data.token, user.userName, user.userSurname, undefined, true, novaFirma);
                dispatch({ type: 'COMPANY', value: novaFirma });

                zatvori();
                history.push('/');
                infoAlarmCheck(data.token);
                planNoviCheck(data.token);

                fetch(process.env.REACT_APP_BACKEND_URL + '/invoice/GetAvansAlarmCount', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + data.token },
                })
                    .then(r => r.json())
                    .then(d => dispatch({ type: 'AVANS_ALARM_SET', value: d?.[0]?.AlarmCount || 0 }))
                    .catch(() => {});

                toast.current?.show({
                    severity: 'success', summary: t('common.uspeh'),
                    detail: t('auth.uspeh'), life: 3000,
                });
            })
            .catch((greska) => {
                const prikazi = (poruka) => toast.current?.show({
                    severity: 'error', summary: t('common.greska'),
                    detail: poruka || t('auth.greskaPromene'), life: 4000,
                });
                if (greska?.json) greska.json().then(m => prikazi(m.message)).catch(() => prikazi());
                else prikazi(greska?.message);
            })
            .finally(() => setMenja(false));
    };

    const izaberi = (firma) => {
        /* Poredjenje preko Number: companyId ume da stigne kao tekst (iz tokena
           ili iz starijeg zapisa u store-u), a value iz baze je broj — strogo
           poredjenje bi tada reklo da su razlicite firme i pustilo "promenu" na
           istu. */
        if (Number(firma.value) === Number(company.companyId)) {
            toast.current?.show({
                severity: 'warn', summary: t('common.upozorenje'),
                detail: t('auth.ista'), life: 3000,
            });
            return;
        }

        zaustaviOdbrojavanje();
        confirmDialog({
            group: 'auth',
            message: t('auth.potvrda', { firma: firma.label }),
            header: t('common.potvrda'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: t('common.da'),
            rejectLabel: t('common.ne'),
            acceptClassName: 'p-button-danger',
            accept: () => promeni(firma),
            reject: nastaviOdbrojavanje,
            onHide: nastaviOdbrojavanje,
        });
    };

    /* Odjava traži potvrdu, iako je „samo jedan klik": dugme stoji u zaglavlju,
       uz promenu firme, i promašaj znači izgubljen rad na otvorenoj formi.
       Potvrda je jeftina — odjava se radi jednom dnevno, forme se gube zauvek. */
    const potvrdiOdjavu = () => {
        zatvori();
        confirmDialog({
            group: 'auth',
            message: t('auth.odjavaNapomena'),
            header: t('auth.odjavaPotvrda'),
            icon: 'pi pi-sign-out',
            acceptLabel: t('auth.odjavaDa'),
            rejectLabel: t('auth.ostaniPrijavljen'),
            acceptClassName: 'p-button-danger',
            accept: () => props.logout(),
        });
    };

    const prebaci = () => {
        if (!imaIzbora) {
            toast.current?.show({
                severity: 'info', summary: t('auth.promenaFirme'),
                detail: t('auth.jednaFirma'), life: 3000,
            });
            return;
        }
        setOtvoren(o => !o);
        setKljucTrake(k => k + 1);
    };

    return (
        <div className="logout">
            <Toast ref={toast} />
            {/* Grupa je OBAVEZNA: zaglavlje je montirano uvek, pa bi bez nje ovaj
                dijalog hvatao i potvrde sa otvorene strane (i obrnuto) — ista
                potvrda bi se iscrtala dvaput. */}
            <ConfirmDialog group="auth" />

            {/* Lična podešavanja u dijalogu: zaglavlje je montirano na svakoj
                strani, pa se profil otvara bez napuštanja posla na kom si. */}
            <Dialog
                visible={profilOtvoren}
                onHide={() => setProfilOtvoren(false)}
                header={t('auth.mojProfil')}
                className="auth-profil-dijalog"
                style={{ width: '46rem' }}
                breakpoints={{ '960px': '92vw' }}
                modal
                draggable={false}
                resizable={false}
            >
                <KorisnikHome />
            </Dialog>

            {/* Ime, firma i odjava stoje kao JEDNA grupa, ne kao tri odvojene
                stvari u redu: sve troje su „moj nalog", pa i izgledaju tako —
                zajednička podloga, pregrada unutra, bez lutajuće crte „|". */}
            <div className={`auth-traka${otvoren ? ' otvorena' : ''}`}>
                <div className="auth-firma-wrap">
                    {/* Pravo dugme, ne tekst sa onClick: tastatura i čitači ekrana
                        inače ne znaju da se tu nešto otvara. */}
                    <button type="button" ref={dugmeRef} className="auth-cip" onClick={prebaci}
                            aria-haspopup="menu" aria-expanded={otvoren}
                            title={imaIzbora ? t('auth.promenaFirme') : company?.companyName}>
                        {znakAvatara('auth-avatar')}
                        <span className="auth-cip-tekst">
                            <span className="auth-korisnik">{punoIme}</span>
                            <span className="auth-firma-red">
                                <span className="auth-firma">{company?.companyName}</span>
                                {company?.companyState && (
                                    <span className="auth-drzava">{company.companyState}</span>
                                )}
                            </span>
                        </span>
                        {imaIzbora && <i className={`pi pi-angle-${otvoren ? 'up' : 'down'} auth-cip-strelica`} />}
                    </button>

                    {otvoren && (
                        <div className="auth-panel" ref={panelRef} role="menu"
                             onMouseEnter={zaustaviOdbrojavanje}
                             onMouseLeave={nastaviOdbrojavanje}>
                            {/* Prvi red je RADNJA, ne natpis: ime i firma već stoje na
                                dugmetu trake iznad, pa ih panel ne ponavlja nego nudi
                                lična podešavanja — lozinka, jezik, tema, mobilna
                                aplikacija (ranije tab „Korisnik" u Administraciji). */}
                            <div className="auth-panel-ja">
                                <button type="button" role="menuitem" className="auth-profil"
                                        onClick={() => { setProfilOtvoren(true); zatvori(); }}>
                                    {znakAvatara('auth-avatar veliki')}
                                    <span className="auth-panel-ja-tekst">
                                        <strong>{t('auth.mojProfil')}</strong>
                                        <span>{punoIme}</span>
                                    </span>
                                    <i className="pi pi-angle-right auth-profil-strelica" />
                                </button>
                                <button type="button" className="auth-zatvori" onClick={zatvori}
                                        aria-label={t('common.close')}>
                                    <i className="pi pi-times" />
                                </button>
                            </div>

                            <div className="auth-panel-glava">
                                <span className="auth-mikro">{t('auth.promenaFirme')}</span>
                                <span className="auth-broj">{firme.length}</span>
                            </div>

                            <ul className="auth-spisak">
                                {firme.map((f) => {
                                    const trenutna = Number(f.value) === Number(company.companyId);
                                    return (
                                        <li key={f.value}>
                                            <button type="button" role="menuitem" disabled={menja}
                                                    className={`auth-stavka${trenutna ? ' trenutna' : ''}`}
                                                    onClick={() => izaberi(f)}>
                                                <span className="auth-stavka-znak" aria-hidden="true"
                                                      style={{ background: bojaImena(f.label) }}>
                                                    {znakFirme(f.label)}
                                                </span>
                                                <span className="auth-stavka-ime">{f.label}</span>
                                                {trenutna && <em><i className="pi pi-check" /> {t('auth.trenutna')}</em>}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>

                            {/* Podnožje postoji SAMO dok promena traje. Objašnjenje da
                                se panel sam zatvara je izbačeno — traka ispod to već
                                pokazuje, a tekst koji se čita jednom zauzima mesto svaki
                                put. */}
                            {menja && (
                                <div className="auth-podnozje">
                                    <span className="auth-menjam"><i className="pi pi-spin pi-spinner" /> {t('auth.menjam')}</span>
                                </div>
                            )}

                            {/* Traka pokazuje KOLIKO je ostalo do zatvaranja. Bez nje se
                                zatvaranje doživljava kao kvar; ovako se vidi da je rok,
                                i da staje čim je miš nad spiskom. */}
                            <span key={kljucTrake} className="auth-odbrojavanje"
                                  style={{ animationDuration: `${ZATVARANJE_MS}ms` }} />
                        </div>
                    )}
                </div>

                {/* Pravo dugme, ne `div` sa onClick: tastatura, fokus i čitači
                    ekrana inače ne vide radnju. Natpis se na uskom ekranu sklanja,
                    ikona ostaje — da odjava ne pojede mesto nazivu firme. */}
                <button type="button" className="auth-odjava" onClick={potvrdiOdjavu}
                        title={t('common.logout')}>
                    <i className="pi pi-sign-out" />
                    <span className="auth-odjava-tekst">{t('common.logout')}</span>
                </button>
            </div>
        </div>
    );
};

export default Auth;
